import { appConfig } from '../config';

export type ProductImageProfile = 'thumb' | 'large';

function trimTrailingSlashes(s: string): string {
    return s.replace(/\/+$/, '');
}

function trimLeadingSlashes(s: string): string {
    return s.replace(/^\/+/, '');
}

function joinBaseAndPath(base: string, path: string): string {
    return `${trimTrailingSlashes(base)}/${trimLeadingSlashes(path)}`;
}

/** Parse `imageMap[].imageurl` (legacy path segment between season and size). */
export function parseMasterColorFromImageMapUrl(imageurl: string): { masterId: string; colorCode: string } | null {
    const s = (imageurl || '').trim();
    if (!s) {
        return null;
    }
    if (s.indexOf('/') !== -1) {
        const parts = s.split('/').filter(Boolean);
        if (parts.length >= 2) {
            return { masterId: parts[0], colorCode: parts[1] };
        }
    }
    const lastU = s.lastIndexOf('_');
    if (lastU > 0) {
        return { masterId: s.slice(0, lastU), colorCode: s.slice(lastU + 1) };
    }
    return null;
}

/**
 * Query string without leading `?`. Undefined/empty => no query on image URL.
 * - `imageQueryThumbRaw` / `imageQueryLargeRaw` (from REACT_APP_*): raw `a=b&c=d`. Empty string => no query for that variant.
 * - Otherwise: `imageQueryParam` + `imageProfileThumb` / `imageProfileLarge`.
 */
export function getProductImageQueryString(profile: ProductImageProfile): string | undefined {
    const raw =
        profile === 'thumb'
            ? appConfig.media.imageQueryThumbRaw
            : appConfig.media.imageQueryLargeRaw;

    if (raw === '') {
        return undefined;
    }
    if (raw != null && String(raw).length > 0) {
        let x = String(raw).trim();
        if (x.charAt(0) === '?') {
            x = x.slice(1);
        }
        const usp = new URLSearchParams();
        const parsed = new URLSearchParams(x);
        parsed.forEach((value, key) => {
            usp.append(key, value);
        });
        const out = usp.toString();
        return out.length ? out : undefined;
    }

    const param = appConfig.media.imageQueryParam;
    const val =
        profile === 'thumb' ? appConfig.media.imageProfileThumb : appConfig.media.imageProfileLarge;
    if (!param || val === undefined || val === null || String(val) === '') {
        return undefined;
    }
    const usp = new URLSearchParams();
    usp.set(param, String(val));
    return usp.toString();
}

/**
 * Static product image on CDN: `{cdnBase}{season}/{master}_{color}.jpg` + optional `?{query}`.
 */
export function buildProductImageUrl(
    seasonCode: string,
    masterId: string,
    colorCode: string,
    profile: ProductImageProfile
): string {
    const path = `${seasonCode}/${masterId}_${colorCode}.jpg`;
    const base = appConfig.media.cdnImageBaseUrl;
    let url = joinBaseAndPath(base, path);
    const qs = getProductImageQueryString(profile);
    if (qs) {
        url += `?${qs}`;
    }
    return url;
}

/** Alternate list/hover image: `{season}/{master}_{color}_b.jpg` */
export function buildProductAlternateImageUrl(
    seasonCode: string,
    masterId: string,
    colorCode: string,
    profile: ProductImageProfile
): string {
    const path = `${seasonCode}/${masterId}_${colorCode}_b.jpg`;
    const base = appConfig.media.cdnImageBaseUrl;
    let url = joinBaseAndPath(base, path);
    const qs = getProductImageQueryString(profile);
    if (qs) {
        url += `?${qs}`;
    }
    return url;
}

/** When `imageMap.imageurl` is a compound path, resolve to full CDN URL. */
export function buildProductImageUrlFromImageMapPath(
    seasonCode: string,
    imageurl: string,
    profile: ProductImageProfile
): string | null {
    const parsed = parseMasterColorFromImageMapUrl(imageurl);
    if (!parsed) {
        return null;
    }
    return buildProductImageUrl(seasonCode, parsed.masterId, parsed.colorCode, profile);
}

/**
 * Pack / gallery image from assets `niceUrl` (e.g. `/S22/1S22A206_8483.jpg`) on CDN + same thumb/large query as product JPGs.
 */
export function buildPackImageUrlFromNiceUrl(
    niceUrl: string,
    profile: ProductImageProfile
): string | null {
    const path = (niceUrl || '').trim();
    if (!path) {
        return null;
    }
    const base = appConfig.media.cdnImageBaseUrl;
    let url = joinBaseAndPath(base, path);
    const qs = getProductImageQueryString(profile);
    if (qs) {
        url += `?${qs}`;
    }
    return url;
}

/** Strip `?profile=…` (or any query) for CDN fallback when profile variant 404s. */
export function stripImageProfileQuery(url: string): string {
    const i = url.indexOf('?');
    return i >= 0 ? url.slice(0, i) : url;
}

/** Alternate season folder: Asset Manager pack index uses `W26/…` while API seasoncode may be `1W26`. */
function seasonFolderCandidates(seasonCode: string): string[] {
    const s = (seasonCode || '').trim();
    const seen = new Set<string>();
    const out: string[] = [];
    const add = (folder: string) => {
        const key = folder.toLowerCase();
        if (folder && !seen.has(key)) {
            seen.add(key);
            out.push(folder);
        }
    };
    add(s);
    if (/^1([A-Za-z]\d.*)$/.test(s)) {
        add(s.slice(1));
    }
    return out;
}

/**
 * Ordered CDN URL candidates for product list images (deduped).
 * Primary with profile → same path without query → alternate season folder variants.
 */
export function buildProductImageUrlCandidates(
    seasonCode: string,
    masterId: string,
    colorCode: string,
    profile: ProductImageProfile
): string[] {
    const candidates: string[] = [];
    const seen = new Set<string>();

    seasonFolderCandidates(seasonCode).forEach((season) => {
        const withProfile = buildProductImageUrl(season, masterId, colorCode, profile);
        if (!seen.has(withProfile)) {
            seen.add(withProfile);
            candidates.push(withProfile);
        }
        const withoutProfile = stripImageProfileQuery(withProfile);
        if (!seen.has(withoutProfile)) {
            seen.add(withoutProfile);
            candidates.push(withoutProfile);
        }
    });

    return candidates;
}
